/**
 * Fichier : chat_widget/src/widget/hooks/useChatTyping.ts
 *
 * Principe :
 * Ce hook gère l'indicateur de saisie (typing) en temps réel via Socket.IO.
 *
 * Il est responsable de :
 * - émettre `typing:start` lorsque l'utilisateur commence à taper
 * - émettre `typing:stop` après un délai d'inactivité (debounce)
 * - sécuriser les émissions (vérification socketConnected + chatId)
 * - nettoyer le timeout lors du démontage du composant
 *
 * Objectif architectural :
 * Isoler complètement la logique d'indicateur de saisie pour éviter toute
 * gestion directe du socket dans `useChatPanel`.
 */
import { useCallback, useEffect, useRef } from "react";
import type { Socket } from "socket.io-client";

type UseChatTypingParams = {
  socketRef: React.RefObject<Socket | null>;
  chatIdRef: React.RefObject<string | null>;
  userId: string;
  sessionId: string;
  socketConnected: boolean;
  debounceMs?: number;
};

export function useChatTyping({
  socketRef,
  chatIdRef,
  sessionId,
  userId,
  socketConnected,
  debounceMs = 900,
}: UseChatTypingParams) {
  const typingTimeout = useRef<number | null>(null);

  const stopTyping = useCallback(() => {
    if (!socketConnected) return;

    const chatId = chatIdRef.current;
    const socket = socketRef.current;
    if (!chatId || !socket) return;

    socket.emit("typing:stop", { chatId, sessionId, userId });
  }, [chatIdRef, sessionId, socketConnected, socketRef, userId]);

  const onUserTyping = useCallback(() => {
    const chatId = chatIdRef.current;
    if (!chatId) return;

    if (!socketConnected) return;

    const socket = socketRef.current;
    if (!socket) return;

    socket.emit("typing:start", { chatId, sessionId, userId });

    if (typingTimeout.current) window.clearTimeout(typingTimeout.current);
    typingTimeout.current = window.setTimeout(() => {
      if (!socketConnected) return;
      const s = socketRef.current;
      if (s) s.emit("typing:stop", { chatId, sessionId, userId });
    }, debounceMs);
  }, [chatIdRef, debounceMs, sessionId, socketConnected, socketRef, userId]);

  useEffect(() => {
    return () => {
      if (typingTimeout.current) window.clearTimeout(typingTimeout.current);
    };
  }, []);

  return {
    onUserTyping,
    stopTyping,
  };
}
